import { useEffect, useState } from "react";
import { MapContainer, Pane, TileLayer, ZoomControl, useMap, useMapEvents } from "react-leaflet";
import { ChoroplethLayer } from "./ChoroplethLayer";
import { CountyChoropleth } from "./CountyChoropleth";
import { TractDrillDown } from "./TractDrillDown";
import { CountySearch } from "./CountySearch";
import { BasemapSwitcher } from "./BasemapSwitcher";
import { LoadingSpinner } from "../shared/LoadingSpinner";
import { ErrorBanner } from "../shared/ErrorBanner";
import { useUIStore, pushUrl } from "../../store/uiStore";
import { useIsMobile } from "../../lib/useIsMobile";
import { useCells } from "../../hooks/useCells";
import type { CountyEntry } from "../../hooks/useCounties";
import { CA_CENTER, CA_ZOOM } from "../../lib/constants";

// Cell-level points are only useful at sufficient zoom — below this they
// pile up on top of the choropleth and clutter the wide-area view.
const CELL_POINTS_MIN_ZOOM = 10;

// MODIS true-color tops out at level 9 (~244 m/px).
const GIBS_MAX_NATIVE_ZOOM = 9;

const TILE_LAYERS = {
  street: {
    light: {
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    },
    dark: {
      url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · © <a href="https://carto.com/attributions">CARTO</a>',
    },
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
  },
  satelliteTime: {
    urlTemplate:
      "https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/MODIS_Terra_CorrectedReflectance_TrueColor/default/{date}/GoogleMapsCompatible_Level9/{z}/{y}/{x}.jpg",
    attribution:
      'Imagery courtesy NASA EOSDIS GIBS — MODIS Terra Corrected Reflectance (True Color)',
  },
} as const;

function tickDateString(tick: string | null): string {
  if (tick) {
    const m = tick.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  return new Date().toISOString().slice(0, 10);
}

export function RiskMap() {
  const { activeTick, drillCounty, setDrillCounty } = useUIStore();
  const theme = useUIStore((s) => s.theme);
  const basemap = useUIStore((s) => s.basemap);
  const riskOverlayVisible = useUIStore((s) => s.riskOverlayVisible);
  const isMobile = useIsMobile();
  const { geojson, isLoading, error } = useCells(activeTick);
  const [zoom, setZoom] = useState<number>(CA_ZOOM);
  const [flyTarget, setFlyTarget] = useState<CountyEntry | null>(null);

  const handleCountyPick = (entry: CountyEntry) => {
    setDrillCounty(entry.geoid);
    pushUrl({ drillCounty: entry.geoid });
    setFlyTarget(entry);
  };

  // Resolve the active tile config — satellite-time substitutes the date
  // from activeTick into the GIBS URL template.
  const tickDate = tickDateString(activeTick);
  let tile: { url: string; attribution: string; maxNativeZoom?: number };
  let tileKey: string;
  if (basemap === "satellite") {
    tile = TILE_LAYERS.satellite;
    tileKey = "satellite";
  } else if (basemap === "satellite-time") {
    tile = {
      url: TILE_LAYERS.satelliteTime.urlTemplate.replace("{date}", tickDate),
      attribution: TILE_LAYERS.satelliteTime.attribution,
      maxNativeZoom: GIBS_MAX_NATIVE_ZOOM,
    };
    // Re-key per date so leaflet refetches when the user switches ticks.
    tileKey = `gibs-${tickDate}`;
  } else {
    tile = theme === "dark" ? TILE_LAYERS.street.dark : TILE_LAYERS.street.light;
    tileKey = `street-${theme}`;
  }

  return (
    <div style={{ position: "relative", flex: 1 }}>
      <MapContainer
        center={CA_CENTER}
        zoom={CA_ZOOM}
        style={{ height: "100%", width: "100%" }}
        // Disable default zoom control so we can place it on the right and
        // free up the top-left for CountySearch.
        zoomControl={false}
      >
        <TileLayer
          key={tileKey}
          attribution={tile.attribution}
          url={tile.url}
          maxNativeZoom={tile.maxNativeZoom}
        />
        <ZoomControl position="topright" />

        <ZoomReporter onChange={setZoom} />
        <FlyController target={flyTarget} />

        {/* Eye-toggle switches the pane's opacity/pointer-events imperatively
            (see RiskOverlayVisibility) so we don't unmount ~5k CircleMarkers. */}
        <Pane name="riskOverlay" style={{ transition: "opacity 0.15s ease-out" }}>
          {!drillCounty && <CountyChoropleth />}
          {drillCounty && <TractDrillDown />}
          {/* Cell-level points only render at deep zoom — see comment on
              CELL_POINTS_MIN_ZOOM. */}
          {geojson && zoom >= CELL_POINTS_MIN_ZOOM && (
            <ChoroplethLayer features={geojson.features} />
          )}
        </Pane>
        <RiskOverlayVisibility visible={riskOverlayVisible} />
      </MapContainer>

      <CountySearch onSelect={handleCountyPick} />
      <BasemapSwitcher />

      {zoom < CELL_POINTS_MIN_ZOOM && !isMobile && (
        <div
          style={{
            position: "absolute",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 600,
            padding: "5px 10px",
            background: "rgba(31, 41, 55, 0.78)",
            color: "#e5e7eb",
            fontSize: 11,
            borderRadius: 14,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
          }}
        >
          Zoom in to inspect individual cells (z ≥ {CELL_POINTS_MIN_ZOOM})
        </div>
      )}

      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 900,
            background: "rgba(255,255,255,0.85)",
            borderRadius: 8,
            padding: 20,
          }}
        >
          <LoadingSpinner size={40} />
        </div>
      )}

      {error && (
        <div style={{ position: "absolute", top: 60, left: 10, zIndex: 900, maxWidth: 300 }}>
          <ErrorBanner message="Could not load cell data." />
        </div>
      )}
    </div>
  );
}

// <Pane>'s style prop only applies on creation; live toggling reaches into
// the leaflet pane directly.
function RiskOverlayVisibility({ visible }: { visible: boolean }) {
  const map = useMap();
  useEffect(() => {
    const pane = map.getPane("riskOverlay");
    if (!pane) return;
    pane.style.opacity = visible ? "1" : "0";
    pane.style.pointerEvents = visible ? "auto" : "none";
  }, [map, visible]);
  return null;
}

// Reports the leaflet map's current zoom level via callback. Mounted as a
// child of <MapContainer> so the react-leaflet hooks can grab the map ref.
function ZoomReporter({ onChange }: { onChange: (z: number) => void }) {
  const map = useMap();
  useMapEvents({
    zoomend: () => onChange(map.getZoom()),
  });
  return null;
}

// flyToBounds the map whenever a new county target is set from the search.
// Caps zoom so we don't fly past where county-level analysis stops being
// useful; users can still zoom in further to surface cell points.
function FlyController({ target }: { target: CountyEntry | null }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    map.flyToBounds(target.bounds, { maxZoom: 9, padding: [40, 40] });
  }, [target, map]);
  return null;
}
